import { ConfigReader } from '../config/ConfigReader';
import { AdminApiDomain, getAdminApiUrl } from '../config/ApiRouting';
import { FulfillmentV2Launch } from './FulfillmentV2Launch';

export interface StockEventRowResponse {
  movementId: string;
  type: 'INBOUND' | 'OUTBOUND';
  qty: number;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED';
  trackingNumber?: string;
  carrier?: string;
  createdAt: string;
  locationId?: string;
  auditor: string;
  orderId?: string;
}

export interface StockStatisticResponse {
  totalQuantity: number;
  totalReservedQuantity: number;
  byLocation: Array<{
    locationId: string;
    quantity: number;
    reservedOutbound: number;
  }>;
}

export interface MonthlyStockSummary {
  month: string;
  inboundQty: number;
  outboundQty: number;
  endingQty: number;
}

export interface StockHistoryStatusResponse {
  statistic: StockStatisticResponse;
  monthly: MonthlyStockSummary[];
}

export interface GetStockHistoryResponse {
  stockStatus: StockHistoryStatusResponse;
  events: StockEventRowResponse[];
  nextToken?: string;
}

export interface StockResponse {
  movementId: string;
  success: boolean;
  message: string;
}

export interface ProductLookupResponse {
  productId: number;
  sku: string;
  name: string;
  nameKor?: string;
  brand?: string;
  productLink?: string;
  productImageUrls?: string[];
  category?: string[];
  size?: string;
  pack?: string;
  originalPrice?: number;
  salePrice?: number;
  currency?: string;
  status?: string;
  description?: string;
  barcode?: string;
}

/**
 * A product as the admin route returns it. `nameKor` and `attributes` are the two columns the
 * customer-facing `/products` route sanitizes away, and `attributes.barcode` is where the catalog
 * actually keeps a barcode — there is no flat `barcode` column to read.
 */
export interface AdminProductLookupResponse extends Omit<ProductLookupResponse, 'barcode'> {
  attributes?: Record<string, string>;
}

/** One row of the sourcing mapper's Korean ↔ WordPress brand table (`/sourcing/krBrandToWp`). */
export interface BrandMappingResponse {
  brandKr: string;
  brandEn: string;
  brandSlug: string;
}

export interface DeleoInboundRequest {
  productId: number;
  qty: number;
  trackingNumber?: string;
  carrier?: string;
  effectiveAt?: string;
  locationId?: string;
  orderId?: string;
  itemId?: string;
  auditor: string;
}

export interface InboundStockRequest {
  productId: number;
  qty: number;
  trackingNumber?: string;
  carrier?: string;
  effectiveAt?: string;
  locationId?: string;
  orderId?: string;
  itemId?: string;
  auditor: string;
}

export enum OutboundStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  INBOUND_CANCEL = 'INBOUND_CANCEL',
  OUTBOUND_CANCEL = 'OUTBOUND_CANCEL',
}

export interface OutboundStockRequest {
  productId: number;
  qty: number;
  trackingNumber?: string;
  carrier?: string;
  locationId?: string;
  status?: OutboundStatus;
  orderId?: string;
  itemId?: string;
  auditor: string;
}

export interface StockLocationInfo {
  locationId: string;
  quantity: number;
  reservedOutbound: number;
}

export interface DetailedStockStatus {
  totalQuantity: number;
  totalReservedQuantity: number;
  byLocation: StockLocationInfo[];
  asOf: string;
}

export interface ExportedStock {
  productId?: number | string;
  sku?: string;
  totalQuantity?: number;
  totalReservedQuantity?: number;
  locationQuantities?: Record<string, number>;
  updatedAt?: string;
}

export interface ExportStockStatusResponse {
  stocks?: ExportedStock[];
}

export interface StockStatusResponse {
  productStocks: Record<string, DetailedStockStatus>;
}

/** The server's own sentence from a refused call, so a 409 reads as the shortfall it names rather than "Conflict". */
const refusalMessage = async (response: Response): Promise<string | undefined> => {
  const body = (await response.json().catch(() => null)) as { message?: string | string[] } | null;
  const text = Array.isArray(body?.message) ? body.message.join(' · ') : body?.message;
  return text || undefined;
};

/** What the operator reads when a V1 stock write is refused after the Fulfillment 2.0 launch (SSD-7433). */
export const RETIRED_V1_STOCK_WRITE_MESSAGE =
  'Nothing was changed: this V1 stock action is retired since the Fulfillment 2.0 launch. Use Fulfillment → Inventory → Stock Adjustment, or the 2.0 order screens.';

export class StockClient {
  private readonly baseUrl: string;
  // /stock/* routes to internal-api; /products?skus= (product lookup) stays on facade (not on prod internal-api).
  private readonly stockAdminBaseUrl: string;
  private readonly apiKey: string;

  constructor() {
    const config = ConfigReader.getInstance().getConfig();
    this.baseUrl = config.apiUrl;
    this.stockAdminBaseUrl = getAdminApiUrl(AdminApiDomain.PRODUCT);
    this.apiKey = config.apiKey;
  }

  public async getStockHistory(
    params: { productId?: number; sku?: string },
    limit?: number,
    nextToken?: string,
  ): Promise<GetStockHistoryResponse> {
    const searchParams = new URLSearchParams();

    if (params.productId) {
      searchParams.append('productId', params.productId.toString());
    }
    if (params.sku) {
      searchParams.append('sku', params.sku);
    }
    if (limit) {
      searchParams.append('limit', limit.toString());
    }
    if (nextToken) {
      searchParams.append('nextToken', nextToken);
    }

    const endpoint = `/stock/history?${searchParams.toString()}`;

    return this.makeStockRequest<GetStockHistoryResponse>(endpoint);
  }

  /** Resolve one catalog SKU; callers may cancel superseded or timed-out validation reads. */
  public async getProductBySku(sku: string, signal?: AbortSignal): Promise<ProductLookupResponse> {
    const searchParams = new URLSearchParams();
    searchParams.append('skus', sku);

    const endpoint = `/products?${searchParams.toString()}`;

    return (await this.makeRequest<ProductLookupResponse[]>(endpoint, signal ? { signal } : {}))[0]!;
  }

  public async getProductsBySkus(skus: string[]): Promise<ProductLookupResponse[]> {
    const searchParams = new URLSearchParams();
    searchParams.append('skus', skus.join(','));

    const endpoint = `/products?${searchParams.toString()}`;

    return this.makeRequest<ProductLookupResponse[]>(endpoint);
  }

  /**
   * Products for an admin screen, from the internal-api route that keeps the admin-only columns.
   * The customer-facing `/products` lookup drops `nameKor` and `attributes`, so a screen that needs
   * the Korean name or a barcode has to come here. The barcode is flattened out of the attributes.
   */
  public async getAdminProductsBySkus(
    skus: string[],
    { fresh = false }: { fresh?: boolean } = {},
  ): Promise<ProductLookupResponse[]> {
    if (skus.length === 0) {
      return [];
    }

    const searchParams = new URLSearchParams();
    searchParams.append('skus', skus.join(','));

    const endpoint = `/products/admin?${searchParams.toString()}`;
    // A check right before a write must not be answered from the browser cache.
    const products = await this.makeStockRequest<AdminProductLookupResponse[]>(
      endpoint,
      fresh ? { cache: 'no-store' } : {},
    );

    return products.map(({ attributes, ...product }) => {
      const barcode = attributes?.['barcode']?.trim();
      return { ...product, ...(barcode ? { barcode } : {}) };
    });
  }

  /**
   * The Korean brand name the sourcing mapper keeps for a WordPress brand slug, or null when it keeps none.
   * The catalog itself carries the brand in English only, so a Korean product name reads with an English brand
   * unless this table is asked (SSD-7389).
   */
  public async getBrandKr(brandSlug: string): Promise<string | null> {
    const searchParams = new URLSearchParams({ brandSlug });
    const mapping = await this.makeStockRequest<Partial<BrandMappingResponse> | null>(
      `/sourcing/krBrandToWp?${searchParams.toString()}`,
    );
    const brandKr = mapping?.brandKr?.trim();
    return brandKr ? brandKr : null;
  }

  public async deleoInbound(request: DeleoInboundRequest): Promise<StockResponse> {
    return this.makeStockWrite<StockResponse>('/stock/deleoInbound', request);
  }

  public async inboundStock(request: InboundStockRequest): Promise<StockResponse> {
    return this.makeStockWrite<StockResponse>('/stock/inbound', request);
  }

  public async outboundStock(request: OutboundStockRequest): Promise<StockResponse> {
    return this.makeStockWrite<StockResponse>('/stock/outbound', request);
  }

  public async getStockStatus(skus: string[]): Promise<StockStatusResponse> {
    const searchParams = new URLSearchParams();
    searchParams.append('skus', skus.join(','));

    const endpoint = `/stock/status?${searchParams.toString()}`;
    return this.makeStockRequest<StockStatusResponse>(endpoint);
  }

  public async getReadyToBeOutbonded(): Promise<import('./OMSClient').OrderListResponse> {
    return this.makeStockRequest<import('./OMSClient').OrderListResponse>('/stock/readyToBeOutbonded');
  }

  /** The export endpoint answers JSON; the Fulfillment 2.0 screen renders its own CSV from these rows. */
  public async exportStockStatusRows(): Promise<ExportStockStatusResponse> {
    const url = `${this.stockAdminBaseUrl}/stock/exportStockStatus`;
    const response = await fetch(url, {
      headers: {
        'X-API-KEY': this.apiKey,
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  public async exportStockStatus(): Promise<Blob> {
    const url = `${this.stockAdminBaseUrl}/stock/exportStockStatus`;
    const response = await fetch(url, {
      headers: {
        'X-API-KEY': this.apiKey,
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.blob();
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.makeRequestTo<T>(this.baseUrl, endpoint, options);
  }

  private async makeStockRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.makeRequestTo<T>(this.stockAdminBaseUrl, endpoint, options);
  }

  private async makeStockWrite<T>(endpoint: string, request: unknown): Promise<T> {
    return this.makeRequestTo<T>(
      this.stockAdminBaseUrl,
      endpoint,
      { method: 'POST', body: JSON.stringify(request) },
      true,
    );
  }

  private async makeRequestTo<T>(
    base: string,
    endpoint: string,
    options: RequestInit = {},
    v1StockWrite = false,
  ): Promise<T> {
    const url = `${base}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        'X-API-KEY': this.apiKey,
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (v1StockWrite && response.status === 410) {
      // The launch guard answers 410 on V1 stock writes: the write is retired, so this screen is too.
      FulfillmentV2Launch.markLaunched();
      throw new Error(RETIRED_V1_STOCK_WRITE_MESSAGE);
    }
    if (!response.ok) {
      throw new Error((await refusalMessage(response)) ?? `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }
}
